import {
  AgendaSlotType,
  BUSINESS_TIMEZONE,
  FULLVISIO_SLOT_TYPE,
  isFullvisioSlotType,
  PRESENTIEL_SLOT_TYPE,
  WorkSchedulesFrequency,
} from '@omecare/utils';
import { isValid, toDate } from 'date-fns';
import { intlFormat } from 'date-fns/intlFormat';
import { CalendarOff, Trash2Icon, UserPlus, VideoIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RhythmDisplay } from '@/components/forms/rhythm-fields';
import { InlineEditInput } from '@/components/inline-edit/inline-edit-input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet } from '@/components/ui/sheet';
import { GetWorkScheduleSlotItemResponse } from '@/hooks/queries/use-get-office-work-schedules-slots';
import { useAuthorization } from '@/hooks/use-authorization';
import { useDateFnsLocale } from '@/hooks/use-date-fns-locale';
import { capitalizeFirst } from '@/lib/capitalize-first';
import { TONES } from '@/lib/tones';
import { isSlotWithAssistant } from '@/utils/slot-assistant';
import { selectSheetViewModel } from '../sheet.viewmodel';
import { formatSlotSchedule } from '../slot-sheet/format-slot-schedule';
import { SlotAssistantHoursSection } from '../slot-sheet/slot-assistant-hours-section';
import { SlotSheetCheckboxRow } from '../slot-sheet/slot-sheet-checkbox-row';
import { SlotSheetHeader } from '../slot-sheet/slot-sheet-header';
import { SlotSheetMetaRow } from '../slot-sheet/slot-sheet-meta-row';
import {
  SlotSheetDefinitionList,
  SlotSheetRow,
  SlotSheetSection,
} from '../slot-sheet/slot-sheet-section';
import {
  SlotSheetActions,
  SlotSheetBody,
  SlotSheetContent,
} from '../slot-sheet/slot-sheet-shell';
import {
  selectViewAdminWorkScheduleSlotSheetViewModel,
  ViewAdminWorkScheduleSlotSheetApi,
  ViewWorkScheduleSlotEventOccurrence,
} from './view-admin-work-schedule-slot-sheet.viewmodel';

export const ViewAdminWorkScheduleSlotSheet = ({
  slot,
  officeId,
  eventOccurrence,
  onAfterDelete,
  onAfterUpdated,
}: {
  slot: GetWorkScheduleSlotItemResponse;
  officeId: string;
  eventOccurrence: ViewWorkScheduleSlotEventOccurrence;
} & Partial<ViewAdminWorkScheduleSlotSheetApi>) => {
  const { t } = useTranslation();
  const locale = useDateFnsLocale();
  const { canEditWorkSchedules, canDeleteSlots } = useAuthorization();
  const canEdit = canEditWorkSchedules();
  const canDelete = canDeleteSlots();

  const sheetViewModel = selectSheetViewModel({
    sheetName: 'ViewAdminWorkScheduleSlotSheet',
  });

  const viewModel = selectViewAdminWorkScheduleSlotSheetViewModel({
    slot,
    officeId,
    eventOccurrence,
    sheetViewModel,
    onAfterDelete: onAfterDelete ?? (() => {}),
    onAfterUpdated: onAfterUpdated ?? (() => {}),
  });

  // Remote (fullvisio) slots are always "sans assistant·e", so the SA override
  // is forced on and not editable here. Read from the view-model's slot snapshot
  // so it reflects an in-sheet mode change immediately.
  const currentSlotType = viewModel.data.slot.slotType;
  const isFullvisio = isFullvisioSlotType(currentSlotType);

  const schedule = formatSlotSchedule({
    start: eventOccurrence.start,
    end: eventOccurrence.end,
    allDay: eventOccurrence.allday,
    locale,
  });

  const frequencyType = useMemo<WorkSchedulesFrequency | null>(() => {
    if (!slot.sequence.rrule) {
      return null;
    }

    if (slot.sequence.rrule.interval === 1) {
      return 'weekly';
    }
    return 'every-2-weeks';
  }, [slot.sequence]);

  const canShowSequence =
    !!slot.rrule && !!slot.sequence && isValid(toDate(slot.sequence.startAt));

  const showAssistantHours = isSlotWithAssistant({
    withAssistant: viewModel.data.slot.withAssistante,
    forceWithoutAssistant: viewModel.information.forceWithoutAssistant,
    slotType: currentSlotType,
  });

  const showRhythm =
    viewModel.information.onSiteSlotsWithoutAssistant != null ||
    viewModel.information.onSiteSlotsWithAssistant != null;

  return (
    <Sheet
      open={sheetViewModel.data.show}
      onOpenChange={sheetViewModel.api.onOpenChange}
    >
      <SlotSheetContent size="detail">
        <SlotSheetHeader
          status="availability"
          title={t(viewModel.title.key, viewModel.title.params)}
          dateLabel={schedule.dateLabel}
          timeLabel={schedule.timeLabel}
        >
          <SlotSheetMetaRow
            id={slot.id}
            updatedAt={slot.updatedAt}
          />

          {canShowSequence && (
            <p className="text-muted-foreground text-xs">
              {t('view-admin-work-schedule-slot-sheet.description-sequence')}{' '}
              <span className="capitalize">
                {intlFormat(slot.sequence.startAt, {
                  timeZone: BUSINESS_TIMEZONE,
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </span>
              {' · '}
              {t(`period-of-day.${slot.sequence.periodOfTheDay}`)}
              {frequencyType && (
                <>
                  {' '}
                  {' · '}
                  {t(`work-schedule-frequency.${frequencyType}`)}
                </>
              )}
            </p>
          )}
        </SlotSheetHeader>

        <SlotSheetBody>
          <SlotSheetSection title={t('slot-sheet.section.slot')}>
            <SlotSheetDefinitionList>
              <SlotSheetRow label={t('slot-sheet.field.period')}>
                {capitalizeFirst(t(`period-of-day.${slot.periodOfTheDay}`))}
                {schedule.timeLabel && (
                  <span className="text-muted-foreground">
                    {' '}
                    · {schedule.timeLabel}
                  </span>
                )}
              </SlotSheetRow>

              <SlotSheetRow
                label={t('view-admin-work-schedule-slot-sheet.slot-type.label')}
              >
                <div className="flex items-center gap-2">
                  {isFullvisio && (
                    <VideoIcon
                      className="size-3.5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  <Select
                    value={currentSlotType}
                    disabled={
                      !canEdit || viewModel.data.updateAssistantModeLoading
                    }
                    onValueChange={(value) =>
                      viewModel.api.updateConsultationMode(
                        value as AgendaSlotType,
                      )
                    }
                  >
                    <SelectTrigger
                      size="sm"
                      className="w-44"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PRESENTIEL_SLOT_TYPE}>
                        {t(
                          'view-admin-work-schedule-slot-sheet.slot-type.presentiel',
                        )}
                      </SelectItem>
                      <SelectItem value={FULLVISIO_SLOT_TYPE}>
                        {t(
                          'view-admin-work-schedule-slot-sheet.slot-type.fullvisio',
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </SlotSheetRow>

              <SlotSheetCheckboxRow
                label={t(
                  'add-office-work-schedule-slot-form.force-without-assistant.label',
                )}
                description={t(
                  'add-office-work-schedule-slot-form.force-without-assistant.description',
                )}
                checked={
                  viewModel.information.forceWithoutAssistant || isFullvisio
                }
                disabled={
                  !canEdit ||
                  viewModel.data.updateAssistantModeLoading ||
                  isFullvisio
                }
                onCheckedChange={viewModel.api.updateForceWithoutAssistant}
              />

              <SlotSheetRow
                label={t('view-admin-work-schedule-slot-sheet.tag.label')}
              >
                {canEdit ? (
                  <InlineEditInput
                    value={viewModel.data.slot.tag || ''}
                    onSave={(value: string) =>
                      viewModel.api.updateTag(value || undefined)
                    }
                    isLoading={viewModel.data.updateTagLoading}
                    placeholder={t(
                      'view-admin-work-schedule-slot-sheet.tag.placeholder',
                    )}
                    maxLength={10}
                    inputClassName="h-8 w-32"
                  />
                ) : (
                  <span>{viewModel.data.slot.tag || '-'}</span>
                )}
              </SlotSheetRow>

              {showRhythm && (
                <SlotSheetRow
                  label={t(
                    'view-admin-work-schedule-slot-sheet.information.doctor-rhythm',
                  )}
                  alignTop
                >
                  <RhythmDisplay
                    onSiteSlotsWithoutAssistant={
                      viewModel.information.onSiteSlotsWithoutAssistant
                    }
                    remoteSlotsWithoutAssistant={
                      viewModel.information.remoteSlotsWithoutAssistant
                    }
                    onSiteSlotsWithAssistant={
                      viewModel.information.onSiteSlotsWithAssistant
                    }
                    remoteSlotsWithAssistant={
                      viewModel.information.remoteSlotsWithAssistant
                    }
                    rhythmNote={viewModel.information.rhythmNote}
                  />
                </SlotSheetRow>
              )}
            </SlotSheetDefinitionList>
          </SlotSheetSection>

          {showAssistantHours && (
            <SlotAssistantHoursSection
              assistantPeriods={slot.assistantPeriods ?? []}
              legacyWorkPeriods={slot.medicalAssistantWorkPeriods}
            />
          )}
        </SlotSheetBody>

        {(canDelete || (canEdit && viewModel.data.isOnLeave)) && (
          <SlotSheetActions className="flex flex-col gap-3">
            {canEdit && viewModel.data.isOnLeave && (
              <>
                <div
                  className={`flex items-center gap-2 rounded-md border p-3 text-sm ${TONES.amber.surfaceBorder} ${TONES.amber.pill} ${TONES.amber.text}`}
                >
                  <CalendarOff
                    className="size-4 shrink-0"
                    aria-hidden="true"
                  />
                  {t(
                    'view-admin-work-schedule-slot-sheet.convert.on-leave-notice',
                  )}
                </div>
                <Button
                  className="w-full"
                  onClick={viewModel.api.convertToOpenSlot}
                  disabled={
                    viewModel.data.convertLoading ||
                    viewModel.data.convertSuccess
                  }
                >
                  <UserPlus
                    className="size-4"
                    aria-hidden="true"
                  />
                  {t('view-admin-work-schedule-slot-sheet.convert.btn')}
                </Button>
              </>
            )}

            {canDelete && (
              <Button
                variant="destructive"
                className="w-full"
                onClick={viewModel.deleteSlot.api.open}
              >
                <Trash2Icon
                  className="size-4"
                  aria-hidden="true"
                />
                {t('view-admin-agenda-slot-sheet.btn.delete-slot')}
              </Button>
            )}
          </SlotSheetActions>
        )}
      </SlotSheetContent>
    </Sheet>
  );
};
